import PropTypes from 'prop-types';
import React, { useState, useEffect } from "react";
import 'react-calendar/dist/Calendar.css';
import Calendar from "react-calendar";
import "./DateRangeSelect.scss";

/**
 * Formats a date range for display in dropdown label
 */
export const formatDateRangeForDropdown = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    
    // If same year, omit year from start date
    if (start.getFullYear() === end.getFullYear()) {
        const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const endStr = end.toLocaleDateString('en-US', options);
        return `${startStr} - ${endStr}`;
    }
    
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
};

const DateRangeSelect = (props) => {
    const { 
        id, 
        onRangeConfirm,
        onCancel,
        initialRange, 
        allowPast,
        isOpen,
        anchorElement
    } = props;

    const [startDate, setStartDate] = useState(
        initialRange && initialRange.from ? new Date(initialRange.from) : null
    );
    const [endDate, setEndDate] = useState(
        initialRange && initialRange.to ? new Date(initialRange.to) : null
    );
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const popupRef = React.useRef(null);

    // Update internal state when initialRange changes
    useEffect(() => {
        if (initialRange && initialRange.from && initialRange.to) {
            setStartDate(new Date(initialRange.from));
            setEndDate(new Date(initialRange.to));
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    }, [initialRange]);

    // Calculate position when popup opens and update on scroll
    useEffect(() => {
        if (isOpen && anchorElement) {
            const updatePosition = () => {
                const rect = anchorElement.getBoundingClientRect();
                setPosition({
                    top: rect.bottom + 8, // 8px below the dropdown
                    left: rect.left
                });
            };

            updatePosition();

            // Update position on scroll and resize
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen, anchorElement]);

    // Handle clicks outside the popup
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                handleCancel();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleStartDateChange = (date) => {
        setStartDate(date);
        // If end date is before new start date, clear end date
        if (endDate && date > endDate) {
            setEndDate(null);
        }
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        // If start date is after new end date, clear start date
        if (startDate && date < startDate) {
            setStartDate(null);
        }
    };

    const handleConfirm = () => {
        if (startDate && endDate && onRangeConfirm) {
            const from = new Date(startDate);
            const to = new Date(endDate);
            
            // Set times: from = start of day, to = end of day
            from.setHours(0, 0, 0, 0);
            to.setHours(23, 59, 59, 999);
            
            onRangeConfirm(id, { from: from.getTime(), to: to.getTime() });
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    // Calculate min/max dates for each calendar
    const getMinDateForStart = () => {
        if (!allowPast) return new Date();
        if (endDate) {
            // Can't select a start date after the end date
            return null;
        }
        return null;
    };

    const getMaxDateForStart = () => {
        // Can't select a start date after the end date
        return endDate || null;
    };

    const getMinDateForEnd = () => {
        // Can't select an end date before the start date
        if (startDate) return startDate;
        if (!allowPast) return new Date();
        return null;
    };

    const getMaxDateForEnd = () => {
        if (startDate) {
            // Can't select an end date before the start date
            return null;
        }
        return null;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="close-calendar-overlay"></div>
            <div className="dual-calendar-popup" ref={popupRef} style={{ top: `${position.top}px`, left: `${position.left}px` }}>
                <div className="calendar-header">
                    <h3>Select Date Range</h3>
                </div>
                <div className="calendars-container">
                    <div className="calendar-section">
                        <label className="calendar-label">Start Date</label>
                        <Calendar 
                            onChange={handleStartDateChange}
                            value={startDate}
                            minDate={getMinDateForStart()}
                            maxDate={getMaxDateForStart()}
                            minDetail="year"
                        />
                    </div>
                    <div className="calendar-section">
                        <label className="calendar-label">End Date</label>
                        <Calendar 
                            onChange={handleEndDateChange}
                            value={endDate}
                            minDate={getMinDateForEnd()}
                            maxDate={getMaxDateForEnd()}
                            minDetail="year"
                        />
                    </div>
                </div>
                <div className="calendar-actions">
                    <button 
                        className="btn-cancel"
                        onClick={handleCancel}
                        type="button"
                    >
                        Cancel
                    </button>
                    <button 
                        className="btn-confirm"
                        onClick={handleConfirm}
                        disabled={!startDate || !endDate}
                        type="button"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </>
    );
};

DateRangeSelect.propTypes = {
    id: PropTypes.string,
    onRangeConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    initialRange: PropTypes.shape({
        from: PropTypes.number,
        to: PropTypes.number
    }),
    allowPast: PropTypes.bool,
    isOpen: PropTypes.bool,
    anchorElement: PropTypes.object
};

DateRangeSelect.defaultProps = {
    id: "date_range_selector",
    onRangeConfirm: null,
    onCancel: null,
    initialRange: null,
    allowPast: true,
    isOpen: false,
    anchorElement: null
};

export default DateRangeSelect;
